import { api } from "../../lib/api";
import type { CouponType } from "../../../../src/types";
import { useState } from "react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { useToast } from "../../hooks/use-toast";
import { useAuth } from "../../lib/authContext";

export const CouponForm = ({
  couponToEdit,
  onSuccess,
}: {
  couponToEdit: CouponType | null;
  setCouponToEdit: (coupon: CouponType | null) => void;
  onSuccess: () => void;
}) => {
  const [formData, setFormData] = useState<CouponType>(
    couponToEdit || {
      code: "",
      min_cart_value: 0,
      discount_type: "",
      discount_value: 0,
      expires_at: new Date().toISOString(),
      is_active: true,
      max_discount_amount: 0,
      starts_at: new Date().toISOString(),
      usage_limit: 0,
      user_usage_limit: 0,
      is_first_order_only: false,
      provider_access_type: "all",
    },
  );
  const { toast } = useToast();
  const { user } = useAuth();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    let response: any;
    if (couponToEdit) {
      response = await api.updateCoupon({
        ...formData,
        updated_by: user?.id,
      });
    } else {
      response = await api.addCoupon({
        ...formData,
        created_by: user?.id,
        updated_by: user?.id,
      });
    }
    if (response.success) {
      onSuccess();
      toast({
        title: `Coupon ${formData.code} ${
          couponToEdit ? "updated" : "created"
        } successfully`,
      });
    } else {
      toast({
        title: `Failed to ${couponToEdit ? "update" : "create"} coupon ${
          formData.code
        }`,
        description: response.error,
        variant: "destructive",
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div className="grid gap-4">
        <div className="grid gap-2">
          <Label htmlFor="code">Code</Label>
          <Input
            type="text"
            name="code"
            value={formData.code}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="flex gap-2">
          <div className="w-1/2">
            <Label htmlFor="discount_type">Discount Type</Label>
            <Select
              name="discount_type"
              value={formData.discount_type}
              onValueChange={value =>
                setFormData({ ...formData, discount_type: value })
              }
              required
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a discount type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="percentage">Percentage</SelectItem>
                <SelectItem value="fixed_amount">Fixed Amount</SelectItem>
                <SelectItem value="free_shipping">Free Shipping</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-1/2">
            <Label htmlFor="discount_value">Discount Value</Label>
            <Input
              type="number"
              name="discount_value"
              value={formData.discount_value}
              onChange={handleInputChange}
            />
          </div>
        </div>
        <div className="flex gap-2">
          <div className="w-1/2">
            <Label htmlFor="min_cart_value">Min Cart Value</Label>
            <Input
              type="number"
              name="min_cart_value"
              value={formData.min_cart_value}
              onChange={handleInputChange}
            />
          </div>
          <div className="w-1/2">
            <Label htmlFor="max_discount_amount">Max Discount Amount</Label>
            <Input
              type="number"
              name="max_discount_amount"
              value={formData.max_discount_amount}
              onChange={handleInputChange}
            />
          </div>
        </div>
        <div className="flex gap-2">
          <div className="w-1/2">
            <Label htmlFor="starts_at">Starts At</Label>
            <Input
              type="date"
              name="starts_at"
              value={
                formData.starts_at
                  ? new Date(formData.starts_at).toISOString().split("T")[0]
                  : ""
              }
              onChange={handleInputChange}
            />
          </div>
          <div className="w-1/2">
            <Label htmlFor="expires_at">Expires At</Label>
            <Input
              type="date"
              name="expires_at"
              value={
                formData.expires_at
                  ? new Date(formData.expires_at).toISOString().split("T")[0]
                  : ""
              }
              onChange={handleInputChange}
            />
          </div>
        </div>
        <div className="flex gap-2">
          <div className="w-1/2">
            <Label htmlFor="usage_limit">Usage Limit</Label>
            <Input
              type="number"
              name="usage_limit"
              value={formData.usage_limit}
              onChange={handleInputChange}
            />
          </div>
          <div className="w-1/2">
            <Label htmlFor="user_usage_limit">User Usage Limit</Label>
            <Input
              type="number"
              name="user_usage_limit"
              value={formData.user_usage_limit}
              onChange={handleInputChange}
            />
          </div>
        </div>
        <div className="flex w-1/2">
          <Input
            type="checkbox"
            name="is_first_order_only"
            checked={formData.is_first_order_only}
            onChange={handleInputChange}
            className="w-4 h-4"
          />
          <Label htmlFor="is_first_order_only" className="ml-2">
            Is Applicable only on First Order?
          </Label>
        </div>
      </div>
      <Button type="submit">Submit</Button>
    </form>
  );
};
